import { GppGoodOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { ChangeEvent } from 'react';
import React from 'react';

import { useCopy } from 'new/hooks/useCopy';
import { HelmCommandPreview } from 'new/modules/settings/capsules/components/HelmCommandPreview/HelmCommandPreview';
import { CapsuleDetailsSection } from 'new/modules/settings/capsules/screens/CapsuleDetailScreen/components/CapsuleDetailsSection';
import { KeyRevealField } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/components/KeyRevealField';
import { useRegisterCapsule } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/context/RegisterCapsuleModalContext';
import { formatTableRegistered } from 'new/modules/settings/capsules/domain';
import type { KeyVisibilityField } from 'new/modules/settings/capsules/utils/capsuleHelpers';
import styles from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/components/CopyKeysStep/CopyKeysStep.module.scss';

const COPY_KEYS_STEP_FIELDS: readonly {
  copyTrackingId: string;
  label: string;
  visibilityField: KeyVisibilityField;
}[] = [
  {
    visibilityField: 'apiAccessKey',
    copyTrackingId: 'apiAccessKey',
    label: 'API Access Key',
  },
  {
    visibilityField: 'registrationKey',
    copyTrackingId: 'registrationKey',
    label: 'Encryption/Decryption Key',
  },
];

function stringOrUndefined(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;
  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

export function CopyKeysStep() {
  const { state, actions } = useRegisterCapsule();
  const {
    generatedKeys,
    keyVisibility,
    hasSavedKeys,
    capsuleDescription,
    createdClusterId,
    createdCapsule,
  } = state;
  const { toggleKeyVisibility, setHasSavedKeys } = actions;
  const { copy, isCopied } = useCopy();

  if (!generatedKeys) {
    return (
      <Typography variant="body2">
        Keys are unavailable. Please go back and generate keys again.
      </Typography>
    );
  }

  const handleCopy = (fieldKey: string, fieldValue: string) => {
    void copy(fieldValue, fieldKey);
  };

  const handleSavedKeysChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHasSavedKeys(event.target.checked);
  };

  const capsuleDescriptionValue =
    stringOrUndefined(createdCapsule?.description) ?? stringOrUndefined(capsuleDescription);

  return (
    <div className={styles.container}>
      <CapsuleDetailsSection
        capsuleId={
          createdCapsule?.id != null
            ? String(createdCapsule.id)
            : createdClusterId != null
              ? String(createdClusterId)
              : undefined
        }
        uid={stringOrUndefined(createdCapsule?.uid)}
        orgId={createdCapsule?.org_id != null ? String(createdCapsule.org_id) : undefined}
        createdAt={
          createdCapsule?.created_at ? formatTableRegistered(createdCapsule.created_at) : undefined
        }
        description={capsuleDescriptionValue}
      />

      <div className={styles.accessCredentialsPanel}>
        <div className={styles.accessCredentialsHeader}>
          <GppGoodOutlined className={styles.accessCredentialsIcon} aria-hidden />
          <Typography variant="subtitle1" component="h3" className={styles.accessCredentialsTitle}>
            Access Credentials
          </Typography>
        </div>
        <div className={styles.accessCredentialsBody}>
          {COPY_KEYS_STEP_FIELDS.map((field) => {
            const fieldValue = generatedKeys[field.visibilityField];
            const isFieldVisible = keyVisibility[field.visibilityField];

            return (
              <KeyRevealField
                key={field.visibilityField}
                label={field.label}
                value={fieldValue}
                isVisible={isFieldVisible}
                isCopied={isCopied(field.copyTrackingId)}
                onToggleVisibility={() => {
                  toggleKeyVisibility(field.visibilityField);
                }}
                onCopy={() => {
                  handleCopy(field.copyTrackingId, fieldValue);
                }}
              />
            );
          })}
        </div>
      </div>

      <HelmCommandPreview
        generatedKeys={generatedKeys}
        dataplaneUid={createdCapsule?.uid}
        orgId={createdCapsule?.org_id}
        capsuleName={createdCapsule?.name}
      />

      <Typography variant="body2" className={styles.deployHint}>
        Copy the command above and run it in your own Kubernetes environment to install the Capsule.
        Nexla does not deploy it for you.
      </Typography>

      <div className={styles.warningBanner}>
        <WarningAmberOutlined className={styles.warningIcon} />
        <div className={styles.warningText}>
          <Typography className={styles.warningTitle}>These keys are shown only once</Typography>
          <Typography className={styles.warningDescription}>
            Copy and store both the API Access Key and the Encryption/Decryption Key securely now.
            Once you close this dialog, Nexla will not show these values again. If you lose them,
            you will need to rotate your keys and redeploy your Capsule.
          </Typography>
          <FormControlLabel
            className={styles.acknowledgeFormControl}
            control={
              <Checkbox size="small" checked={hasSavedKeys} onChange={handleSavedKeysChange} />
            }
            label={
              <Typography variant="subtitle2" component="span" className={styles.acknowledgeLabel}>
                Confirm you have stored both keys
              </Typography>
            }
          />
        </div>
      </div>
    </div>
  );
}
